import { RasterPixels } from './types';

// The artwork the share is taken of, as analyzeImageDataStats counts it.
const ALPHA_OPAQUE = 153;
// Un-premultiplying a partly see-through pixel rounds its colour by a level or two, which reads as a
// gradient along every anti-aliased edge: only near-solid pixels are compared.
const ALPHA_SOLID = 250;
// A Sobel magnitude above this, in any channel, is a colour edge; a hard edge reaches four times its step.
const EDGE_MIN = 80;
// JPEG ringing beside a colour edge changes colour for a few pixels.
const EDGE_REACH = 6;
// Pixels each side of the square the colour change is read over.
const WINDOW_RADIUS = 3;
// Least mean squared Sobel magnitude over the square, summed over the channels: a magnitude of 2 in
// one channel. A two-colour gradient across the raster moves a channel half a level a pixel, 4.
const MIN_ENERGY = 4;
// How much the colour changes one way across the square (1) rather than every way (0), as noise does.
const MIN_COHERENCE = 0.7;

// Each value replaced by the sum over the square `radius` around it, in place.
function sumOverSquare(values: Float64Array, line: Float64Array, width: number, height: number, radius: number) {
  const sumLine = (start: number, step: number, length: number) => {
    for (let i = 0; i < length; i += 1) {
      line[i] = values[start + i * step];
    }
    let sum = 0;
    for (let i = 0; i < Math.min(radius, length); i += 1) {
      sum += line[i];
    }
    for (let i = 0; i < length; i += 1) {
      if (i + radius < length) {
        sum += line[i + radius];
      }
      if (i - radius - 1 >= 0) {
        sum -= line[i - radius - 1];
      }
      values[start + i * step] = sum;
    }
  };
  for (let y = 0; y < height; y += 1) {
    sumLine(y * width, 1, width);
  }
  for (let x = 0; x < width; x += 1) {
    sumLine(x, width, height);
  }
}

// Writes 1 to `out` where a pixel up to EDGE_REACH away along the line is 1.
function dilateLines(src: Uint8Array, out: Uint8Array, width: number, height: number, horizontal: boolean) {
  const lineCount = horizontal ? height : width;
  const lineLength = horizontal ? width : height;
  const step = horizontal ? 1 : width;
  for (let line = 0; line < lineCount; line += 1) {
    const start = horizontal ? line * width : line;
    let lastSet = -Infinity;
    for (let i = 0; i < lineLength; i += 1) {
      if (src[start + i * step]) {
        lastSet = i;
      }
      out[start + i * step] = i - lastSet <= EDGE_REACH ? 1 : 0;
    }
    lastSet = Infinity;
    for (let i = lineLength - 1; i >= 0; i -= 1) {
      if (src[start + i * step]) {
        lastSet = i;
      }
      if (lastSet - i <= EDGE_REACH) {
        out[start + i * step] = 1;
      }
    }
  }
}

/**
 * Writes the Sobel derivatives of `values` at `p` to `out` ([x, y]). A neighbour outside the
 * artwork reads as the pixel itself, so the artwork's outline is no edge.
 */
function sobel(values: Float64Array, solid: Uint8Array, p: number, width: number, out: Float64Array) {
  const own = values[p];
  const tl = solid[p - width - 1] ? values[p - width - 1] : own;
  const tc = solid[p - width] ? values[p - width] : own;
  const tr = solid[p - width + 1] ? values[p - width + 1] : own;
  const ml = solid[p - 1] ? values[p - 1] : own;
  const mr = solid[p + 1] ? values[p + 1] : own;
  const bl = solid[p + width - 1] ? values[p + width - 1] : own;
  const bc = solid[p + width] ? values[p + width] : own;
  const br = solid[p + width + 1] ? values[p + width + 1] : own;
  out[0] = tl + 2 * ml + bl - (tr + 2 * mr + br);
  out[1] = tl + 2 * tc + tr - (bl + 2 * bc + br);
}

/**
 * The share of the artwork in smooth colour gradients, which embroidery can't sew. A pixel counts
 * when the colour around it changes steadily one way and no colour edge is near. Each channel is
 * read on its own, as a gradient between two colours of the same lightness changes hue only. The
 * change is read on the colours averaged over 3×3 pixels, so the noise of compression or dither on
 * a gradient averages out, and noise on a flat colour, changing every way, does not count. The
 * artwork's own outline is no edge, so gradient text counts as much as a gradient panel.
 */
function measureGradientShare({ data, width, height }: RasterPixels): number {
  const pixelCount = width * height;
  const solid = new Uint8Array(pixelCount);
  let opaque = 0;
  for (let p = 0; p < pixelCount; p += 1) {
    const alpha = data[p * 4 + 3];
    opaque += alpha >= ALPHA_OPAQUE ? 1 : 0;
    solid[p] = alpha >= ALPHA_SOLID ? 1 : 0;
  }
  if (!opaque) {
    return 0;
  }

  const line = new Float64Array(Math.max(width, height));
  const solidNear = Float64Array.from(solid);
  sumOverSquare(solidNear, line, width, height, 1);
  const channels = [0, 1, 2].map(() => new Float64Array(pixelCount));
  const averaged = [0, 1, 2].map(() => new Float64Array(pixelCount));
  for (let channel = 0; channel < 3; channel += 1) {
    const raw = channels[channel];
    const average = averaged[channel];
    for (let p = 0; p < pixelCount; p += 1) {
      raw[p] = data[p * 4 + channel];
      average[p] = solid[p] ? raw[p] : 0;
    }
    sumOverSquare(average, line, width, height, 1);
    for (let p = 0; p < pixelCount; p += 1) {
      average[p] = solid[p] ? average[p] / solidNear[p] : 0;
    }
  }

  // the structure tensor of the averaged colour change, summed over the channels
  const xx = new Float64Array(pixelCount);
  const yy = new Float64Array(pixelCount);
  const xy = new Float64Array(pixelCount);
  const edge = new Uint8Array(pixelCount);
  const gradient = new Float64Array(2);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = y * width + x;
      if (solid[p]) {
        for (let channel = 0; channel < 3; channel += 1) {
          sobel(channels[channel], solid, p, width, gradient);
          if (gradient[0] ** 2 + gradient[1] ** 2 > EDGE_MIN ** 2) {
            edge[p] = 1;
          }
          sobel(averaged[channel], solid, p, width, gradient);
          xx[p] += gradient[0] ** 2;
          yy[p] += gradient[1] ** 2;
          xy[p] += gradient[0] * gradient[1];
        }
      }
    }
  }

  const solidInWindow = Float64Array.from(solid);
  [xx, yy, xy, solidInWindow].forEach((values) => sumOverSquare(values, line, width, height, WINDOW_RADIUS));
  const rowNearEdge = new Uint8Array(pixelCount);
  const nearEdge = new Uint8Array(pixelCount);
  dilateLines(edge, rowNearEdge, width, height, true);
  dilateLines(rowNearEdge, nearEdge, width, height, false);

  let smooth = 0;
  for (let p = 0; p < pixelCount; p += 1) {
    const energy = xx[p] + yy[p];
    if (solid[p] && !nearEdge[p] && energy >= MIN_ENERGY * solidInWindow[p]) {
      const coherence = Math.sqrt((xx[p] - yy[p]) ** 2 + 4 * xy[p] ** 2) / energy;
      smooth += coherence >= MIN_COHERENCE ? 1 : 0;
    }
  }
  return smooth / opaque;
}

export default measureGradientShare;
